import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle, Phone, Mail, User, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../services/api';

export const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    preferred_contact: 'email'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/contact/submit', formData);
      setReferenceId(response.data.reference_id);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      preferred_contact: 'email'
    });
    setSubmitted(false);
    setReferenceId('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-[#111111] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 bg-[#111111] border-b border-white/10 p-6 flex items-center justify-between z-10">
            <div>
              <h2 className="text-2xl font-bold text-white font-['Cormorant_Garamond']">Contact Us</h2>
              <p className="text-white/50 text-sm mt-1">We'll respond within 24 hours</p>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 bg-[#4ADE80]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#4ADE80]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 font-['Cormorant_Garamond']">Message Sent!</h3>
                <p className="text-white/60 mb-6">
                  Thank you for reaching out. We'll get back to you within 24 hours.
                </p>
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 mb-6">
                  <p className="text-white/50 text-sm mb-1">Your Reference Number</p>
                  <p className="text-[#D4AF37] font-mono font-bold text-lg">{referenceId}</p>
                </div>
                <Button
                  onClick={handleClose}
                  className="bg-[#D4AF37] hover:bg-[#E6C965] text-black font-semibold rounded-full px-8"
                >
                  Close
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <Label className="text-white/80 font-medium flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" /> Your Name
                  </Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="h-12 bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-xl"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label className="text-white/80 font-medium flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4" /> Email Address
                  </Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                    className="h-12 bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-xl"
                    data-testid="contact-email-input"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label className="text-white/80 font-medium flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4" /> Phone Number <span className="text-white/30">(Optional)</span>
                  </Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="h-12 bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-xl"
                    data-testid="contact-phone-input"
                  />
                </div>

                {/* Preferred Contact */}
                <div>
                  <Label className="text-white/80 font-medium mb-2 block">Preferred Contact Method</Label>
                  <div className="flex gap-3">
                    {['email', 'phone', 'either'].map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, preferred_contact: method }))}
                        className={`flex-1 py-3 px-4 rounded-xl border transition-all ${
                          formData.preferred_contact === method
                            ? 'bg-[#D4AF37] border-[#D4AF37] text-black font-semibold'
                            : 'bg-[#1A1A1A] border-white/10 text-white/70 hover:border-white/30'
                        }`}
                      >
                        {method.charAt(0).toUpperCase() + method.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <Label className="text-white/80 font-medium flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4" /> Subject
                  </Label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    required
                    className="h-12 bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-xl"
                    data-testid="contact-subject-input"
                  />
                </div>

                {/* Message */}
                <div>
                  <Label className="text-white/80 font-medium mb-2 block">Message</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    required
                    rows={4}
                    className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-xl resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-[#D4AF37] hover:bg-[#E6C965] text-black font-semibold rounded-xl transition-all"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Sending...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </div>
                  )}
                </Button>

                {/* Direct Contact Info */}
                <div className="pt-4 border-t border-white/10 text-center">
                  <p className="text-white/40 text-sm mb-2">Or reach us directly at</p>
                  <a 
                    href="mailto:nchelphub@outlook.com" 
                    className="text-[#D4AF37] hover:text-[#E6C965] font-medium transition-colors"
                  >
                    nchelphub@outlook.com
                  </a>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContactFormModal;
